/**
 * The my-orders tab table: ONE row per (channel, tab). It replaces what v1 spelled out five times
 * (`getSendData`'s two switches, `loadOrders`'s if-chain, both containers' option lists,
 * `MY_ORDER_SIDE_TABS`, the four overview lists).
 *
 * Rows are in web's sidebar order with web's labels, so mobile gains `prepared` and both
 * `advance-order` tabs, which it lacked.
 *
 * A leaf: constants only, so it loads under `node --test` without a store.
 *
 * ⚠️ `storeKey` keeps v1's slice field names. Two things the names hide:
 *   - `preparingOrdersInfo` holds ACCEPTED orders (tab "prep"), not "Prepared" ones — those are
 *     in `preparedOrdersInfo`.
 *   - every instore tab AND both `advance-order` tabs share `openOrdersInfo`, so a tab switch
 *     REPLACES the list rather than filling a sibling. That is why `storeKeyFor(tab)` needs no
 *     channel — no tab id maps to two keys.
 */
import { ADVANCE_ORDER, DINE_IN } from "../utils/constants/shared";
import { VIEW_CLOSED_ORDERS } from "../utils/restrictionConstants/shared";

export type Channel = "delivery" | "instore";

export type StoreKey =
  | "overviewOrdersInfo"
  | "newOrdersInfo"
  | "preparingOrdersInfo"
  | "preparedOrdersInfo"
  | "processedOrdersInfo"
  | "dispatchOrdersInfo"
  | "deliveredOrdersInfo"
  | "cancelledOrdersInfo"
  | "openOrdersInfo"
  /** the search results: no tab; `useOrderSearch` fills it */
  | "searchOrdersInfo";

export interface Bucket {
  /** v1's `orderType` / `selectedOption` — the id both apps already route on */
  tab: string;
  channel: Channel;
  label: string;
  /** what this tab adds to the base `sale/filter` body (see `getSendData`) */
  query: Readonly<Record<string, string | number>>;
  /** the slice field the saga writes this tab's response into */
  storeKey: StoreKey;
  /** `fulfillmentStatus` values from the overview fetch that this tab's column shows */
  overviewStatuses?: readonly string[];
  /** the overview column's heading when it is not just the label — web's copy */
  overviewLabel?: string;
  /** `featureRestrictions` role that must pass for the tab to be offered */
  feature?: string;
}

const open = { storeKey: "openOrdersInfo" } as const;

export const BUCKETS: readonly Bucket[] = [
  { tab: "overview", channel: "delivery", label: "Overview", query: { pageSize: 1000, fulfillmentStatus: "Overview" }, storeKey: "overviewOrdersInfo" },
  // web's overview also counts "Confirmed" as new; the tab fetch asks the server for "Created" only
  { tab: "new", channel: "delivery", label: "New Orders", query: { fulfillmentStatus: "Created" }, storeKey: "newOrdersInfo", overviewStatuses: ["Created", "Confirmed"] },
  { tab: "prep", channel: "delivery", label: "Accepted", query: { fulfillmentStatus: "Accepted" }, storeKey: "preparingOrdersInfo", overviewStatuses: ["Accepted"], overviewLabel: "Accepted/ Preparing" },
  { tab: "prepared", channel: "delivery", label: "Prepared", query: { fulfillmentStatus: "Prepared" }, storeKey: "preparedOrdersInfo", overviewStatuses: ["Prepared"] },
  { tab: "processed", channel: "delivery", label: "Processed", query: { fulfillmentStatus: "Processed" }, storeKey: "processedOrdersInfo", overviewStatuses: ["Processed"] },
  { tab: "dispatch", channel: "delivery", label: "Dispatched", query: { fulfillmentStatus: "Dispatched" }, storeKey: "dispatchOrdersInfo" },
  { tab: "delivered", channel: "delivery", label: "Delivered", query: { fulfillmentStatus: "Delivered" }, storeKey: "deliveredOrdersInfo" },
  { tab: "cancelled", channel: "delivery", label: "Cancelled", query: { fulfillmentStatus: "Cancelled" }, storeKey: "cancelledOrdersInfo" },
  { tab: ADVANCE_ORDER, channel: "delivery", label: "Advance Orders", query: { fulfillmentStatus: "Advance Order" }, ...open },
  { tab: "open-instore", channel: "instore", label: "Open", query: { saleStatus: "Open" }, ...open },
  { tab: "closed-instore", channel: "instore", label: "Closed", query: { saleStatus: "Closed" }, feature: VIEW_CLOSED_ORDERS, ...open },
  { tab: "cancelled-instore", channel: "instore", label: "Cancelled", query: { saleStatus: "Cancelled" }, ...open },
  { tab: "amt-instore", channel: "instore", label: "Amount Due", query: { saleStatus: "Amount Due" }, feature: VIEW_CLOSED_ORDERS, ...open },
  { tab: ADVANCE_ORDER, channel: "instore", label: "Advance Orders", query: { saleStatus: "Advance Order" }, ...open },
];

/** Where each channel lands when opened, or when the terminal goes offline (instore) — web's rule. */
export const DEFAULT_TAB: Record<Channel, string> = { delivery: "overview", instore: "open-instore" };

/**
 * The my-orders tab + open order, resolved against the live channel.
 *
 * `useMyOrders` keeps the user's pick TAGGED with the channel it was made for and runs it
 * through this on every render, so a channel switch lands on that channel's default with
 * nothing open WITHOUT a state write from an effect (which renders twice and paints one frame
 * of the previous channel's list under the new channel's tabs).
 */
export function pickFor(
  picked: { channel: Channel; tab: string; selected: string | null },
  channel: Channel
): { tab: string; selected: string | null } {
  if (picked.channel === channel) return { tab: picked.tab, selected: picked.selected };
  return { tab: DEFAULT_TAB[channel], selected: null };
}

/** v1 treats anything that is not "delivery" as instore — `getSendData`'s `else` branch. */
export const channelOf = (selectedChannel: string = ""): Channel =>
  selectedChannel === "delivery" ? "delivery" : "instore";

export const bucketsFor = (channel: Channel) => BUCKETS.filter((b) => b.channel === channel);

export const bucketFor = (channel: Channel, tab: string) =>
  BUCKETS.find((b) => b.channel === channel && b.tab === tab);

/** The tabs a user may see: a `feature`-gated row needs its role in the user's allowed features. */
export const visibleBuckets = (channel: Channel, allowedFeatures: readonly string[] = []) =>
  bucketsFor(channel).filter((b) => !b.feature || allowedFeatures.includes(b.feature));

/** The lists the local store answers offline: every instore tab and both advance-order tabs (`./localOrders`). */
export const isFacadeList = (tab: string) => storeKeyFor(tab) === "openOrdersInfo";

/**
 * The request payload for a list fetch. Both loader keys carry the same value: web's interceptor
 * reads `skipGlobalLoader`, mobile's `hideLoader`.
 */
export const listPayload = (channel: Channel, tab: string, quiet: boolean) => ({
  data: getSendData(tab, channel),
  skipGlobalLoader: quiet,
  hideLoader: quiet,
});

/** The overview's columns, in display order. */
export const OVERVIEW_COLUMNS = BUCKETS.filter((b) => b.overviewStatuses);

export const storeKeyFor = (tab: string) => BUCKETS.find((b) => b.tab === tab)?.storeKey;

/**
 * The instore list query as the LOCAL store wants it, for the offline lane — ONE definition,
 * used by v2's offline lane (`./localOrders`) and by the facade's v1 `getOpenOrderFn`, which
 * assigns it over its payload. The API's `saleStatus` becomes the local store's `status`, except
 * that "Cancelled" on a dine-in filter is expressed as a `filterType` and "Amount Due" as
 * `filterType: "amountDue"` + `status: "Closed"`.
 */
export function instoreLocalQuery(data: Record<string, any> = {}): Record<string, any> {
  const patch: Record<string, any> = {};
  if (data.saleStatus === "Cancelled" && data.filterType === "Dine In") patch.filterType = "Cancelled";
  else patch.status = data.saleStatus;
  if (data.saleStatus === "Amount Due") {
    patch.filterType = "amountDue";
    patch.status = "Closed";
  }
  return patch;
}

/** The delivery tab lists: storeKey -> the ONE `fulfillmentStatus` that list holds. */
const LIST_STATUS: Partial<Record<StoreKey, string>> = Object.fromEntries(
  BUCKETS.filter((b) => b.channel === "delivery" && b.storeKey !== "overviewOrdersInfo" && b.storeKey !== "openOrdersInfo").map((b) => [
    b.storeKey,
    b.query.fulfillmentStatus,
  ])
);

/**
 * After a mutation, does the updated order still belong in the list this bucket holds?
 * `orderUpdated` drops a row the change moved on. Only removal is decided here: a destination list
 * is re-fetched when its tab is opened (`useMyOrders.setTab` always loads), so nothing is inserted
 * blind.
 *
 *   - a delivery tab list is exactly one `fulfillmentStatus`: decidable from the order alone
 *   - `openOrdersInfo` holds whichever INSTORE tab was last fetched, and every instore tab
 *     filters on `saleStatus` — so a CHANGED `saleStatus` means it left that list. An unchanged
 *     one cannot be ruled on here ("Amount Due" is Closed + a balance, "Advance Order" is a
 *     different axis), so it stays and the tab's own reload corrects it
 *   - the overview holds every status its four columns claim and `selectOverviewSplit` drops the
 *     rest, so an overview row never has to move — the `byId` patch alone re-columns it
 *   - search results are a query snapshot, as v1 kept them until the next search
 */
export function stillInList(storeKey: StoreKey, order: any, previous: any): boolean {
  const status = LIST_STATUS[storeKey];
  if (status) return order?.fulfillmentStatus === status;
  if (storeKey === "openOrdersInfo") return !previous?.saleStatus || previous.saleStatus === order?.saleStatus;
  return true;
}

/**
 * The `sale/filter` body for a tab + channel: v1's two `switch`es (`helpers/myOrderHelpers`) as a
 * table lookup, with the same output (`buckets.test.ts` holds the captured truth table). An
 * unmatched tab still sends a well-formed query with no status filter.
 */
export function getSendData(orderType: string = "", selectedChannel: string = "") {
  const channel = channelOf(selectedChannel);
  return {
    restrictUserClosedOrders: false,
    restrictUserOthersOrders: false,
    isNew: true,
    filterType: channel === "delivery" ? "Delivery" : DINE_IN,
    ...bucketFor(channel, orderType)?.query,
  } as Record<string, any>;
}
